import {
    CreateExtensionArgument,
    DmApis,
    pointerUtils,
    type SnapshotDal,
    type CreateExtArgs,
    type Extension,
    type ExtensionAPI
} from '@wix/document-manager-core'
import {veloCliService, type VeloCliService} from './utils/veloCliService'
import {importPage} from './utils/importExport'
import {buildDocumentUpdate} from './utils/documentUpdateBuilder'
import type {CompRef, Pointer} from '@wix/document-services-types'
import {getUpdatedPageIds, SnapshotSyncManager} from './utils/snapshots'
import {VIEW_MODES} from '../../constants/constants'
import {EVENTS} from './constants'
import {ReportableError} from '@wix/document-manager-utils'
import type {SnapshotExtApi} from '../snapshots'
import type {DocumentFSUpdate, FullDocument} from '@wix/anyide-cli-types'
import {DocumentUpdateState} from './utils/documentUpdateState'
import type {PageExtensionAPI} from '../page'
import type {ImportExportExtensionApi} from '../import-export/importExportExtension'
import type {FixersExtensionAPI} from '../fixers/fixers'
import {EXCLUDED_NAMESPACES, GLOBAL_NAMESPACES} from '../import-export/json/constants'

const {getPointer} = pointerUtils

const createExtension = ({dsConfig}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = ({
        dal,
        pointers,
        extensionAPI,
        eventEmitter,
        coreConfig
    }: CreateExtArgs): LocalEditorExtensionAPI => {
        const snapshotSyncManager = new SnapshotSyncManager()
        const documentUpdateState = new DocumentUpdateState()
        let initialVersion: string

        let veloCliServiceInstance: VeloCliService

        const importSite = async ({pages}: DocumentFSUpdate, options: ImportSiteOptions = {}) => {
            const pageIds = Object.keys(pages)
            const processedPageIds = []
            for (const [pageId, data] of Object.entries(pages)) {
                const pagePointer = getPointer(pageId, VIEW_MODES.DESKTOP) as CompRef

                await importPage(pagePointer, data, extensionAPI, dal, pointers, coreConfig)

                if (options.onAfterPageImport) {
                    processedPageIds.push(pageId)
                    await options.onAfterPageImport({pagePointer, pageIds, processedPageIds})
                }
            }
        }

        const importSiteConsideringState = async (
            documentUpdate: DocumentFSUpdate,
            options: ImportSiteOptions = {}
        ): Promise<SnapshotDal> => {
            const mergedUpdate = documentUpdateState.append(documentUpdate)
            await importSite(mergedUpdate, options)

            const snapshot = dal.commitTransaction()
            documentUpdateState.clear()
            return snapshot
        }

        const handleSyncFromFileSystem = async (documentUpdate: DocumentFSUpdate) => {
            const snapshot = await importSiteConsideringState(documentUpdate)

            snapshotSyncManager.markSnapshotSyncedFromFS(snapshot)
            eventEmitter.emit(EVENTS.LOCAL_EDITOR.AFTER_SYNC_FROM_FS)
        }

        const getCliWsUrl = (): string => {
            if (!dsConfig.localEditorWsUrl) {
                throw new ReportableError({
                    message: 'localEditorWsUrl was not provided for local editor',
                    errorType: 'missingLocalEditorWsUrl'
                })
            }

            return dsConfig.localEditorWsUrl
        }

        const initialize = async (): Promise<void> => {
            if (!veloCliServiceInstance) {
                veloCliServiceInstance = veloCliService(getCliWsUrl())
            }

            const document: FullDocument = await veloCliServiceInstance.connect()

            initialVersion = document.pages.masterPage.data.version

            veloCliServiceInstance.syncChanges(handleSyncFromFileSystem)

            await importSite(document)
            const snapshot = dal.commitTransaction()

            // after init state is synced
            snapshotSyncManager.markSnapshotSyncedFromFS(snapshot)
            snapshotSyncManager.markSnapshotSyncedFromEditor(snapshot)
        }

        const exportToCli = async (pageIds: CompRef['id'][]) => {
            if (!pageIds.length) {
                return
            }

            const documentUpdate = buildDocumentUpdate(pageIds, dal, extensionAPI)
            await veloCliServiceInstance.save(documentUpdate)

            const {snapshots} = extensionAPI as SnapshotExtApi
            snapshotSyncManager.markSnapshotSyncedFromEditor(snapshots.getCurrentSnapshot())
        }

        const save = async () => {
            const pageIds = getUpdatedPageIds(extensionAPI, snapshotSyncManager)
            await exportToCli(pageIds)
        }

        const fullSave = async () => {
            const {page} = extensionAPI as PageExtensionAPI
            const pageIds = page.getAllPagesIds(true)
            await exportToCli(pageIds)
        }

        const isDataItemRelevant = ({id, type}: Pointer) => {
            if (EXCLUDED_NAMESPACES.has(type)) {
                return false
            }

            const globalNamespace = GLOBAL_NAMESPACES[type]
            if (!globalNamespace) {
                return true
            }

            return globalNamespace === 'All' || globalNamespace.has(id)
        }

        const documentFixesApplied = (): boolean => {
            const {fixers, importExport} = extensionAPI as FixersExtensionAPI & ImportExportExtensionApi
            if (importExport.wml.getVersion() !== initialVersion) {
                return true
            }

            const changedItemsPointers = fixers.getFixersChanges()
            return changedItemsPointers.some(isDataItemRelevant)
        }

        return {
            localEditor: {
                importSite,
                initialize,
                save,
                fullSave,
                documentFixesApplied
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {localEditor} = extensionAPI as LocalEditorExtensionAPI

        return {
            localEditor: {
                save: localEditor.save,
                fullSave: localEditor.fullSave,
                documentFixesApplied: localEditor.documentFixesApplied
            }
        }
    }

    return {
        name: 'localEditor',
        dependencies: new Set(['importExport', 'pages', 'snapshots', 'fixers']),
        createExtensionAPI,
        EVENTS,
        createPublicAPI
    }
}

interface OnAfterPageImportArgs {
    pagePointer: CompRef
    pageIds: string[]
    processedPageIds: string[]
}

interface ImportSiteOptions {
    onAfterPageImport?(args: OnAfterPageImportArgs): Promise<void>
}

export interface LocalEditorExtensionAPI extends ExtensionAPI {
    localEditor: {
        importSite(documentUpdate: DocumentFSUpdate, options?: ImportSiteOptions): Promise<void>
        initialize(): Promise<void>
        save(): Promise<void>
        fullSave(): Promise<void>
        documentFixesApplied(): boolean
    }
}

export {createExtension}
